import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import Icon from '../../components/AppIcon';
import ProgressSummaryCard from './components/ProgressSummaryCard';
import NotificationCenter from './components/NotificationCenter';
import { useUserData } from './hooks/useUserData';
import { useDashboardStats } from './hooks/useDashboardStats';
import { useNotifications } from './hooks/useNotifications';
import { Loader2 } from 'lucide-react';

const StudentDashboard = () => {
  // Load user data
  const { fullName } = useUserData();

  // Load dashboard statistics
  const {
    portfolioProgress,
    completedSections,
    totalSections,
    uploadedDocuments,
    missingElements
  } = useDashboardStats();

  // Load notifications
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    archiveNotification
  } = useNotifications();

  const dynamicActions = [];

  if (missingElements?.[0]) {
    dynamicActions.push({
      title: `Finish ${missingElements[0]?.label}`,
      description: 'Quick win to boost completion',
      icon: 'Sparkles',
      path: '/portfolio-builder',
      color: 'primary',
      progress: portfolioProgress
    });
  }

  if (uploadedDocuments < 3) {
    dynamicActions.push({
      title: 'Upload transcripts',
      description: 'Add 2+ docs to unlock advisor review',
      icon: 'Upload',
      path: '/portfolio-builder',
      color: 'success'
    });
  }

  const actions = (dynamicActions.length ? dynamicActions : null)?.slice(0, 4);

  const momentumStats = [
    { label: 'Portfolio', value: `${portfolioProgress}%`, hint: `${completedSections}/${totalSections} sections`, icon: 'Gauge', color: 'text-primary' },
    { label: 'Unread', value: unreadCount || 0, hint: 'Alerts waiting', icon: 'Bell', color: 'text-warning' },
    { label: 'Focus Areas', value: missingElements?.length || 0, hint: 'Sections to finish', icon: 'Target', color: 'text-secondary' }
  ];


  return (
    <>
      <Helmet>
        <title>Dashboard - UniSteps</title>
        <meta name="description" content="Your student dashboard to track portfolio progress and university applications" />
      </Helmet>
      
      <div className="min-h-screen app-shell">
        <Header />
        
        <main className="pt-20 sm:pt-24 pb-20 sm:pb-14 px-3 sm:px-4 lg:px-8 safe-bottom mobile-scroll">
          <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
            {/* Welcome Section */}
            <div className="surface-card rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 overflow-hidden relative fade-up">
              <div className="absolute inset-0 aurora-blob opacity-40" aria-hidden="true" />
              <div className="absolute inset-0 tile-grid" aria-hidden="true" />
              <div className="relative z-10 flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1.5 sm:space-y-2">
                  <p className="text-xs sm:text-sm uppercase tracking-[0.25em] sm:tracking-[0.3em] text-muted-foreground">Application control room</p>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground headline-serif">
                    Hey {fullName || 'there'}, your runway is clear
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground max-w-3xl">We surfaced the fastest wins to raise your readiness score and keep deadlines in sight.</p>
                </div>
                <div className="glass-panel glass-stroke rounded-lg sm:rounded-xl px-4 py-3 w-full sm:w-auto md:w-64 flex-shrink-0">
                  <p className="text-xs sm:text-sm text-muted-foreground">Readiness score</p>
                  <p className="text-xl sm:text-2xl font-semibold text-foreground">{portfolioProgress}%</p>
                  <p className="text-xs text-muted-foreground mt-0.5 sm:mt-1">{completedSections} of {totalSections} milestones</p>
                </div>
              </div>
            </div>

            {/* Momentum Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {momentumStats.map((item, idx) => (
                <div key={idx} className="bg-card border border-border rounded-lg sm:rounded-xl p-3 sm:p-4 flex items-center gap-3 touch-feedback">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon} size={16} className={`sm:w-[18px] sm:h-[18px] ${item.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-muted-foreground truncate">{item.label}</p>
                    <div className="flex items-baseline gap-1.5 sm:gap-2">
                      <span className="text-lg sm:text-xl font-semibold text-foreground">{item.value}</span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground truncate">{item.hint}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Left Column - Progress */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                <ProgressSummaryCard
                  portfolioProgress={portfolioProgress}
                  missingElements={missingElements}
                  completedSections={completedSections}
                  totalSections={totalSections}
                  uploadedDocuments={uploadedDocuments}
                />
              </div>

              {/* Right Column - Notifications + Next Best Moves */}
              <div className="space-y-4 sm:space-y-6">
                {notificationsLoading ? (
                  <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center h-60 sm:h-80">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <NotificationCenter
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onMarkAsRead={markAsRead}
                    onArchive={archiveNotification}
                  />
                )}

                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                    <h2 className="text-base sm:text-lg font-semibold text-foreground">Next best moves</h2>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Auto-ranked from your gaps</p>
                  </div>
                  <QuickActionPanel actions={actions} />
                </div>
              </div>
            </div>

            {/* Recommendations & Events */}
          </div>
        </main>
      </div>
    </>
  );
};

export default StudentDashboard;